/* Eerie procedural ambient backdrops for Chess and Pente.
 *
 * Pure WebAudio. Two distinct but kindred ambient pads:
 *   • chess — slow minor-key pipe-organ-like drone, occasional reverbed
 *     bell tinkles, glassy "celesta" notes — cool, churchy, austere.
 *   • pente — deeper bone-flute / ney-style breathy drone, lower octave,
 *     hollow knocks, woody resonance — warmer, forest-floor, druidic.
 *
 * One audio context per page. Stops on unmount or theme switch.
 */
import { useEffect } from "react";

const STORAGE_MUTE_KEY = "eerie:muted:v1";
let _ctx = null;

function ctx() {
  if (typeof window === "undefined") return null;
  if (!_ctx) {
    try { _ctx = new (window.AudioContext || window.webkitAudioContext)(); } catch { _ctx = null; }
  }
  return _ctx;
}

export function isEerieMuted() {
  try { return localStorage.getItem(STORAGE_MUTE_KEY) === "yes"; } catch { return false; }
}
export function setEerieMuted(muted) {
  try { localStorage.setItem(STORAGE_MUTE_KEY, muted ? "yes" : "no"); } catch {}
}

// ---------------------------------------------------------------- helpers

function adsr(ctx, gain, t0, a, d, s, r, peak = 1) {
  gain.gain.cancelScheduledValues(t0);
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(peak,     t0 + a);
  gain.gain.exponentialRampToValueAtTime(s * peak, t0 + a + d);
  gain.gain.setValueAtTime(s * peak, t0 + a + d);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + a + d + r);
}

function makeReverb(ctx, seconds = 2.4, decay = 2.2) {
  const rate = ctx.sampleRate;
  const len = Math.floor(rate * seconds);
  const buf = ctx.createBuffer(2, len, rate);
  for (let ch = 0; ch < 2; ch++) {
    const data = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
    }
  }
  const conv = ctx.createConvolver();
  conv.buffer = buf;
  return conv;
}

// Schedule a fade-in/out one-shot tone of the given timbre.
function playTone(ctx, dest, freq, when, dur, { type = "sine", peak = 0.18, attack = 0.04, release = 1.6 } = {}) {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  osc.connect(g);
  g.connect(dest);
  adsr(ctx, g, when, attack, 0.05, 0.7, release, peak);
  osc.start(when);
  osc.stop(when + dur + release + 0.2);
}

// Continuous breathy drone built from filtered noise.
function makeBreathDrone(ctx, dest, freq, type = "sine") {
  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.value = freq;
  const g = ctx.createGain();
  g.gain.value = 0.06;
  // Slow LFO modulation for "breath"
  const lfo = ctx.createOscillator();
  lfo.frequency.value = 0.13 + Math.random() * 0.07;
  const lfoGain = ctx.createGain();
  lfoGain.gain.value = 0.04;
  lfo.connect(lfoGain).connect(g.gain);
  osc.connect(g).connect(dest);
  osc.start();
  lfo.start();
  return { stop: () => { try { osc.stop(); lfo.stop(); } catch {} } };
}

// ---------------------------------------------------------------- voices

function startChessVoice(c, opts = {}) {
  // Per-voice option:
  //   opts.lowOsc — only honored by the legacy Pente proxy below; ignored by
  //   the choral chess voice itself.
  void opts;
  // -------------------------------------------------------------------
  // INTENSE CHORAL MUSIC IN MINOR CHORDS (D minor cycle: i – iv – V – i)
  // Layered "ah" voices: each chord tone is a sine + slightly detuned sine
  // (chorus), routed through a vocal-formant band-pass and a long reverb.
  // Chords change every ~6 s with smooth crossfade.  Slow tremolo on the
  // master gives the choir its breathing intensity.
  // -------------------------------------------------------------------
  const out = c.createGain();
  out.gain.value = 0.0;
  out.connect(c.destination);
  out.gain.linearRampToValueAtTime(0.7, c.currentTime + 2.5);

  // Slow tremolo (~0.45 Hz) modulating the master for rising-and-falling intensity
  const trem = c.createOscillator();
  const tremG = c.createGain();
  trem.type = "sine"; trem.frequency.value = 0.45;
  tremG.gain.value = 0.12;
  trem.connect(tremG).connect(out.gain);
  trem.start();

  const reverb = makeReverb(c, 4.5, 2.6);
  const wet = c.createGain(); wet.gain.value = 0.85;
  reverb.connect(wet).connect(out);
  const dry = c.createGain(); dry.gain.value = 0.55;
  dry.connect(out);

  // ---- Voice factory: returns { setFreq, gain, stop } for one held vocal pad
  // `kind`: "mixed" (default — neutral 720/1150 Hz "ah") or "contralto"
  // (warmer, lower formants ~520/1000 Hz, gentler vibrato).  The synth
  // contralto voices give the choir its dusky lower body.
  const makeVoice = (kind = "mixed") => {
    const isAlto = kind === "contralto";
    const o1 = c.createOscillator();
    const o2 = c.createOscillator();
    o1.type = "sine"; o2.type = "sine";
    o1.detune.value = -7; o2.detune.value = +7; // chorus
    const bp1 = c.createBiquadFilter();
    bp1.type = "bandpass";
    bp1.frequency.value = isAlto ? 520 : 720;
    bp1.Q.value = isAlto ? 5 : 4;
    const bp2 = c.createBiquadFilter();
    bp2.type = "bandpass";
    bp2.frequency.value = isAlto ? 1000 : 1150;
    bp2.Q.value = isAlto ? 6 : 5;
    const mix = c.createGain(); mix.gain.value = 1.0;
    const g = c.createGain(); g.gain.value = 0.0001;
    const vib = c.createOscillator();
    const vibG = c.createGain();
    vib.type = "sine";
    vib.frequency.value = isAlto ? 4.3 : 5.2;
    vibG.gain.value = isAlto ? 1.8 : 2.4;
    vib.connect(vibG).connect(o1.detune);
    vib.connect(vibG).connect(o2.detune);
    o1.connect(mix); o2.connect(mix);
    const a = c.createGain(); const b = c.createGain();
    a.gain.value = isAlto ? 0.6  : 0.55;
    b.gain.value = isAlto ? 0.4  : 0.45;
    mix.connect(bp1).connect(a).connect(g);
    mix.connect(bp2).connect(b).connect(g);
    g.connect(reverb);
    g.connect(dry);
    o1.start(); o2.start(); vib.start();
    const setInitialFreq = (f) => {
      o1.frequency.setValueAtTime(f, c.currentTime);
      o2.frequency.setValueAtTime(f, c.currentTime);
    };
    const setFreq = (f, when = c.currentTime, _ramp = 0) => {
      // STEADY-PITCH chord change: gain dip → instant frequency jump → restore.
      void _ramp;
      const dipDur = 0.35;
      const swapAt = when + dipDur;
      const restoreAt = swapAt + 0.4;
      const target = isAlto ? 0.28 : 0.32;
      g.gain.cancelScheduledValues(when);
      g.gain.setValueAtTime(g.gain.value || 0.0001, when);
      g.gain.linearRampToValueAtTime(0.0001, swapAt - 0.005);
      o1.frequency.cancelScheduledValues(swapAt);
      o2.frequency.cancelScheduledValues(swapAt);
      o1.frequency.setValueAtTime(f, swapAt);
      o2.frequency.setValueAtTime(f, swapAt);
      g.gain.setValueAtTime(0.0001, swapAt);
      g.gain.linearRampToValueAtTime(target, restoreAt);
    };
    return {
      gain: g,
      kind,
      setFreq,
      setInitialFreq,
      stop: () => { try { o1.stop(); o2.stop(); vib.stop(); } catch {} },
    };
  };

  // FOUR mixed voices (S/A/T/B-ish neutral "ah") + TWO synth contraltos
  // for the dusky lower body.  Contraltos double the root and the minor
  // 3rd one octave below the existing voices, giving every minor chord
  // a chest-voice underpinning.  (The "human contralto" experiment was
  // reverted in iter 33 — formant synthesis on its own doesn't read as
  // a real human voice.)
  const voices = [makeVoice(), makeVoice(), makeVoice(), makeVoice()];
  const contraltos = [makeVoice("contralto"), makeVoice("contralto")];

  // Chord progression — D natural minor, ALL MINOR (no major V).
  // Existing voices play the upper four pitches.  Contraltos play the
  // root and minor-third one octave BELOW the existing root, anchoring
  // the harmony in a warm low register.
  const CHORDS = [
    [146.83, 174.61, 220.00, 293.66], // Dm  (D3 F3 A3 D4)
    [196.00, 233.08, 293.66, 392.00], // Gm  (G3 Bb3 D4 G4)
    [220.00, 261.63, 329.63, 440.00], // Am  (A3 C4 E4 A4)
    [164.81, 196.00, 246.94, 329.63], // Em  (E3 G3 B3 E4)
  ];
  // Contralto pitches — one octave below the chord's root and minor 3rd.
  //   Dm → D2 (73.42) + F2 (87.31)
  //   Gm → G2 (98.00) + Bb2 (116.54)
  //   Am → A2 (110.00) + C3 (130.81)
  //   Em → E2 (82.41)  + G2 (98.00)
  const CONTRALTOS = [
    [73.42, 87.31],
    [98.00, 116.54],
    [110.00, 130.81],
    [82.41, 98.00],
  ];

  // Set initial pitches at t0
  voices.forEach((v, i) => {
    v.gain.gain.setValueAtTime(0.0001, c.currentTime);
    v.gain.gain.exponentialRampToValueAtTime(0.32, c.currentTime + 2.6);
    try {
      v.setInitialFreq(CHORDS[0][i]);
    } catch {}
  });
  contraltos.forEach((v, i) => {
    v.gain.gain.setValueAtTime(0.0001, c.currentTime);
    v.gain.gain.exponentialRampToValueAtTime(0.28, c.currentTime + 2.6);
    try {
      v.setInitialFreq(CONTRALTOS[0][i]);
    } catch {}
  });

  let stopped = false;
  let chordIdx = 0;
  // Sped up: each chord holds for ~7 s (was 14 s) so the harmony feels alive.
  const advance = () => {
    if (stopped) return;
    chordIdx = (chordIdx + 1) % CHORDS.length;
    const t = c.currentTime + 0.05;
    voices.forEach((v, i) => v.setFreq(CHORDS[chordIdx][i], t, 4.0));
    contraltos.forEach((v, i) => v.setFreq(CONTRALTOS[chordIdx][i], t, 4.0));
    setTimeout(advance, 7000);
  };
  setTimeout(advance, 7000);

  return () => {
    stopped = true;
    [...voices, ...contraltos].forEach((v) => {
      try {
        v.gain.gain.cancelScheduledValues(c.currentTime);
        v.gain.gain.linearRampToValueAtTime(0.0001, c.currentTime + 1.2);
      } catch {}
    });
    setTimeout(() => [...voices, ...contraltos].forEach((v) => v.stop()), 1500);
    try { trem.stop(); } catch {}
    out.gain.cancelScheduledValues(c.currentTime);
    out.gain.linearRampToValueAtTime(0.0001, c.currentTime + 1.5);
    setTimeout(() => { try { out.disconnect(); } catch {} }, 2000);
  };
}

// Legacy chess organ drone — kept for Pente, which uses a smoothed ("triangle"
// low-osc) variant of the original D-minor pipe-organ pad.
function startOrganVoice(c, opts = {}) {
  const lowOsc = opts.lowOsc || "sawtooth";
  const out = c.createGain();
  out.gain.value = 0.0;
  out.connect(c.destination);
  const t0 = c.currentTime;
  out.gain.linearRampToValueAtTime(0.55, t0 + 2.5);

  const reverb = makeReverb(c, 3.0, 2.8);
  const wet = c.createGain(); wet.gain.value = 0.7;
  reverb.connect(wet).connect(out);
  const dry = c.createGain(); dry.gain.value = 0.5;
  dry.connect(out);

  const drones = [
    makeBreathDrone(c, dry, 73.42,  lowOsc),     // D2
    makeBreathDrone(c, dry, 110.00, "sine"),     // A2
    makeBreathDrone(c, dry, 174.61, "triangle"), // F3
  ];

  let stopped = false;
  const bellTones = [880.00, 1046.50, 1174.66, 783.99, 587.33];
  const tick = () => {
    if (stopped) return;
    const t = c.currentTime + 0.05;
    const f = bellTones[Math.floor(Math.random() * bellTones.length)];
    playTone(c, reverb, f, t, 0.6, { type: "sine", peak: 0.22, attack: 0.005, release: 2.4 });
    if (Math.random() < 0.4) {
      playTone(c, reverb, f * 1.5, t + 0.35, 0.4, { type: "sine", peak: 0.12, attack: 0.005, release: 1.6 });
    }
    setTimeout(tick, 6000 + Math.random() * 8000);
  };
  setTimeout(tick, 4000);

  return () => {
    stopped = true;
    drones.forEach((d) => d.stop());
    out.gain.cancelScheduledValues(c.currentTime);
    out.gain.linearRampToValueAtTime(0.0001, c.currentTime + 1.5);
    setTimeout(() => { try { out.disconnect(); } catch {} }, 2000);
  };
}

// High synth-pad bells for Pente — the same bell tones that used to top
// the legacy Pente organ voice (D-minor adjacent: 587, 783, 880, 1046,
// 1174 Hz), played sparsely.  Used as an *overlay* on top of the chess
// choir so Pente's backdrop reads as more ambient.
function startPenteHighBells(c) {
  const out = c.createGain();
  out.gain.value = 0.0;
  out.connect(c.destination);
  out.gain.linearRampToValueAtTime(0.55, c.currentTime + 2.5);

  const reverb = makeReverb(c, 3.0, 2.8);
  const wet = c.createGain(); wet.gain.value = 0.7;
  reverb.connect(wet).connect(out);

  let stopped = false;
  const bellTones = [880.00, 1046.50, 1174.66, 783.99, 587.33];
  const tick = () => {
    if (stopped) return;
    const t = c.currentTime + 0.05;
    const f = bellTones[Math.floor(Math.random() * bellTones.length)];
    playTone(c, reverb, f, t, 0.6, { type: "sine", peak: 0.22, attack: 0.005, release: 2.4 });
    if (Math.random() < 0.4) {
      playTone(c, reverb, f * 1.5, t + 0.35, 0.4, { type: "sine", peak: 0.12, attack: 0.005, release: 1.6 });
    }
    setTimeout(tick, 6000 + Math.random() * 8000);
  };
  setTimeout(tick, 4000);

  return () => {
    stopped = true;
    out.gain.cancelScheduledValues(c.currentTime);
    out.gain.linearRampToValueAtTime(0.0001, c.currentTime + 1.5);
    setTimeout(() => { try { out.disconnect(); } catch {} }, 2000);
  };
}

// Pente backdrop: chess choir (slow minor-key vocal pads) PLUS the high
// synth-pad bells.  More ambient than the previous organ-drone voice.
function startPenteAmbientVoice(c) {
  const stopChoir = startChessVoice(c);
  const stopBells = startPenteHighBells(c);
  return () => {
    try { stopChoir(); } catch {}
    try { stopBells(); } catch {}
  };
}

function startPenteVoice(c) {
  // Lower B/F# minor 5th drone, breathy bone-flute notes, woody knocks
  const out = c.createGain();
  out.gain.value = 0.0;
  out.connect(c.destination);
  const t0 = c.currentTime;
  out.gain.linearRampToValueAtTime(0.6, t0 + 2.5);

  const reverb = makeReverb(c, 2.0, 2.0);
  const wet = c.createGain(); wet.gain.value = 0.55;
  reverb.connect(wet).connect(out);
  const dry = c.createGain(); dry.gain.value = 0.55;
  dry.connect(out);

  // Drone stack — full B-NATURAL-MINOR triad (B1 / D3 / F#2) for an
  // unambiguous "sad" minor quality (the previous open-fifth lacked the
  // minor 3rd). Smooth waveforms only.
  const drones = [
    makeBreathDrone(c, dry, 61.74,  "sine"),     // B1 — root
    makeBreathDrone(c, dry, 92.50,  "triangle"), // F#2 — fifth
    makeBreathDrone(c, dry, 146.83, "sine"),     // D3 — minor third (the "sad" colour)
    makeBreathDrone(c, dry, 123.47, "sine"),     // B2 — octave for body
  ];

  let stopped = false;
  // ECONOMICAL melodic phrases — instead of one random note every 8-17s, we
  // play a short 3-4 note motif every 14-22s built from the B aeolian scale
  // (B C# D E F# G A B). Each motif descends towards the tonic, giving the
  // figures a sad, sighing shape.
  const SCALE_HZ = {
    B3: 246.94, "C#4": 277.18, D4: 293.66, E4: 329.63, "F#4": 369.99,
    G4: 392.00, A4: 440.00, B4: 493.88, "C#5": 554.37, D5: 587.33, E5: 659.25, "F#5": 739.99,
  };
  const PHRASES = [
    // Sigh from 5 → 3 → root
    [["F#5", 0.0, 1.4], ["D5", 1.4, 1.6], ["B4", 3.2, 2.4]],
    // Stepwise descent
    [["A4", 0.0, 1.0], ["G4", 1.1, 1.0], ["F#4", 2.2, 1.0], ["E4", 3.3, 2.4]],
    // Lonely two-note appoggiatura
    [["E5", 0.0, 0.8], ["D5", 0.9, 2.6]],
    // Rising question, then fall
    [["B4", 0.0, 0.7], ["D5", 0.8, 0.7], ["F#5", 1.6, 0.7], ["E5", 2.4, 2.4]],
  ];
  const tickFlute = () => {
    if (stopped) return;
    const t0 = c.currentTime + 0.05;
    const phrase = PHRASES[Math.floor(Math.random() * PHRASES.length)];
    phrase.forEach(([note, offset, sus]) => {
      const f = SCALE_HZ[note];
      if (f) playTone(c, reverb, f, t0 + offset, sus, {
        type: "triangle",
        peak: 0.16,
        attack: 0.22,
        release: 1.6,
      });
    });
    // Schedule next phrase 14-22s after this one ENDS (economical pacing)
    const phraseLen = Math.max(...phrase.map((p) => p[1] + p[2]));
    setTimeout(tickFlute, (phraseLen + 14 + Math.random() * 8) * 1000);
  };
  // Woody knocks (low filtered noise burst)
  const tickKnock = () => {
    if (stopped) return;
    const t = c.currentTime;
    const buf = c.createBuffer(1, c.sampleRate * 0.18, c.sampleRate);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < ch.length; i++) ch[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / ch.length, 5);
    const src = c.createBufferSource();
    src.buffer = buf;
    const f = c.createBiquadFilter();
    f.type = "lowpass";
    f.frequency.value = 280;
    const g = c.createGain();
    g.gain.value = 0.55;
    src.connect(f).connect(g).connect(reverb);
    src.start(t);
    setTimeout(tickKnock, 11000 + Math.random() * 12000);
  };

  setTimeout(tickFlute, 5000);
  setTimeout(tickKnock, 9000);

  return () => {
    stopped = true;
    drones.forEach((d) => d.stop());
    out.gain.cancelScheduledValues(c.currentTime);
    out.gain.linearRampToValueAtTime(0.0001, c.currentTime + 1.5);
    setTimeout(() => { try { out.disconnect(); } catch {} }, 2000);
  };
}

// ---------------------------------------------------------------- estampie (Checkers)
// Medieval estampie pastiche — drone bagpipe-style fifth (D2/A2) + a brisk
// 6/8 recorder dance line in D dorian + tambour beat on the down-stroke.

function startEstampieVoice(c) {
  const out = c.createGain();
  out.gain.value = 0.0;
  out.connect(c.destination);
  out.gain.linearRampToValueAtTime(0.95, c.currentTime + 2.2);

  const reverb = makeReverb(c, 1.6, 1.6);
  const wet = c.createGain(); wet.gain.value = 0.4;
  reverb.connect(wet).connect(out);

  // Drone + tambour percussion REMOVED per user spec — just the melodic
  // recorder line remains.

  // ---- Recorder melody — D dorian (D E F G A B C D'), tempo ~120 dotted-quarter
  const D = {
    D4: 293.66, E4: 329.63, F4: 349.23, G4: 392.00, A4: 440.00, B4: 493.88, C5: 523.25,
    D5: 587.33, E5: 659.25, F5: 698.46, G5: 783.99, A5: 880.00,
  };
  // Estampie A-section motif (6/8 x 4 bars) followed by descending B-section.
  // Each tuple: (note, beats). Beat = 0.25s (tempo ~ 240 bpm @ eighth).
  const A = [
    ["A4",1],["G4",0.5],["A4",0.5],["B4",1],["A4",1],
    ["G4",1],["F4",0.5],["G4",0.5],["A4",1],["E4",1],
    ["D4",1.5],["E4",0.5],["F4",1],["G4",1],
    ["A4",2],["A4",1],
  ];
  const B = [
    ["D5",1],["C5",0.5],["B4",0.5],["A4",1],["G4",1],
    ["F4",1],["E4",1],["D4",2],
    ["E4",0.5],["F4",0.5],["G4",1],["A4",1],["F4",1],
    ["E4",1.5],["D4",1.5],
  ];
  const FORM = [...A, ...A, ...B, ...A]; // AABA

  const beat = 0.45; // seconds — slowed from 0.25 (was ~240 bpm @ eighth, now ~133)
  let stopped = false;
  let cursor = 0;

  const recorderTone = (freq, when, dur) => {
    const o = c.createOscillator();
    const o2 = c.createOscillator();
    const g = c.createGain();
    o.type = "triangle"; o.frequency.value = freq;
    o2.type = "sine"; o2.frequency.value = freq * 2;  // octave for recorder shimmer
    const o2g = c.createGain(); o2g.gain.value = 0.18;
    g.gain.setValueAtTime(0.0001, when);
    g.gain.exponentialRampToValueAtTime(0.26, when + 0.04);
    g.gain.setValueAtTime(0.26, when + dur - 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, when + dur + 0.15);
    o.connect(g);
    o2.connect(o2g).connect(g);
    g.connect(reverb);
    o.start(when); o.stop(when + dur + 0.2);
    o2.start(when); o2.stop(when + dur + 0.2);
  };

  const tambour = (when) => {
    // Removed per user — kept as a no-op so any stray scheduled call is safe.
    void when;
  };

  const playBar = () => {
    if (stopped) return;
    const startAt = c.currentTime + 0.08;
    let off = 0;
    // Tambour calls intentionally removed from the bar loop.
    for (let i = 0; i < 8; i++) {
      const item = FORM[(cursor + i) % FORM.length];
      const [name, beats] = item;
      const dur = beats * beat;
      const f = D[name]; if (f) recorderTone(f, startAt + off, dur);
      off += dur;
    }
    cursor = (cursor + 8) % FORM.length;
    const nextDelay = (off + 0.05) * 1000;
    setTimeout(playBar, nextDelay);
  };
  setTimeout(playBar, 1500);

  return () => {
    stopped = true;
    out.gain.cancelScheduledValues(c.currentTime);
    out.gain.linearRampToValueAtTime(0.0001, c.currentTime + 1.5);
    setTimeout(() => { try { out.disconnect(); } catch {} }, 2000);
  };
}

// ---------------------------------------------------------------- phantasm (15-tile puzzle)
// Phantasmic swooshes through long reverb + delay — descending pitched-noise
// sweeps and sparse ghostly bell hits, all soaked in echo.

function startPhantasmVoice(c) {
  const out = c.createGain();
  out.gain.value = 0.0;
  out.connect(c.destination);
  out.gain.linearRampToValueAtTime(0.55, c.currentTime + 2.5);

  // LARGE reverb (4 s tail) and a 380ms feedback delay for that haunted-hall echo
  const reverb = makeReverb(c, 4.0, 3.0);
  const wet = c.createGain(); wet.gain.value = 0.85;
  reverb.connect(wet);
  const delay = c.createDelay(2.0);
  delay.delayTime.value = 0.38;
  const fb = c.createGain(); fb.gain.value = 0.45;  // feedback
  delay.connect(fb).connect(delay);
  delay.connect(out);
  wet.connect(delay);
  wet.connect(out);

  // Subtle low-bed (very soft) so the ear has somewhere to rest between swooshes.
  const bedDrone = makeBreathDrone(c, out, 49.00, "sine"); // G1, very low

  let stopped = false;

  // ---- A swoosh: filtered pink noise with a falling band-pass sweep
  const swoosh = ({ rising = false, dur = 1.6, peak = 0.45 } = {}) => {
    const t0 = c.currentTime + 0.04;
    const len = c.sampleRate * (dur + 0.4);
    const buf = c.createBuffer(1, len, c.sampleRate);
    const ch = buf.getChannelData(0);
    let lp = 0;
    for (let i = 0; i < len; i++) {
      const w = Math.random() * 2 - 1;
      lp = 0.96 * lp + 0.04 * w;
      ch[i] = lp * 4;
    }
    const src = c.createBufferSource(); src.buffer = buf;
    const bp = c.createBiquadFilter();
    bp.type = "bandpass";
    bp.Q.value = 6;
    bp.frequency.setValueAtTime(rising ? 320 : 2400, t0);
    bp.frequency.exponentialRampToValueAtTime(rising ? 2800 : 280, t0 + dur);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(peak, t0 + 0.1);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(bp).connect(g).connect(reverb);
    src.start(t0); src.stop(t0 + dur + 0.4);
  };

  // ---- A ghostly bell: short sine carrier — DEEPER + ROUNDER per user
  // ("less toppy, less obtrusive"). FM modulation index reduced so the
  // bell rings cleanly rather than clanging.
  const ghostBell = (freq) => {
    const t0 = c.currentTime + 0.03;
    const o = c.createOscillator();
    const m = c.createOscillator();
    const mg = c.createGain();
    const g = c.createGain();
    // A gentle low-pass takes the squeak off
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 1800;
    o.type = "sine"; o.frequency.value = freq;
    m.type = "sine"; m.frequency.value = freq * 2.0;     // simple octave (was 2.7 = inharmonic)
    mg.gain.value = freq * 0.18;                          // was 0.7 → much rounder
    m.connect(mg).connect(o.frequency);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.42, t0 + 0.005); // slightly louder for elevated intensity
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 3.2); // longer tail
    o.connect(lp).connect(g).connect(reverb);
    o.start(t0); m.start(t0);
    o.stop(t0 + 3.4); m.stop(t0 + 3.4);
  };

  const tick = () => {
    if (stopped) return;
    // Single-note beeps/bloops removed per spec — only swooshes now.
    const rising = Math.random() < 0.4;
    swoosh({
      rising,
      dur: rising ? 1.0 + Math.random() * 0.7 : 1.6 + Math.random() * 1.2,
      peak: rising ? 0.45 : 0.55,
    });
    // Tighter cadence for elevated intensity.
    setTimeout(tick, 2200 + Math.random() * 2800);
  };
  setTimeout(tick, 1500);

  return () => {
    stopped = true;
    bedDrone.stop();
    out.gain.cancelScheduledValues(c.currentTime);
    out.gain.linearRampToValueAtTime(0.0001, c.currentTime + 1.5);
    setTimeout(() => { try { out.disconnect(); } catch {} }, 2000);
  };
}

// ---------------------------------------------------------------- aleatoric (Cursed Hereafter Gallery)
//
// Per user iter 52: scrap the dissonant-string ensemble and rebuild
// the gallery backdrop with EXPERIMENTAL AMBIENT instruments —
// instruments and gestures associated with composers like Aphex Twin's
// SAW2, William Basinski, Tim Hecker, Stars of the Lid:
//   • Singing-bowl drones (FM-bell synthesis: sine carrier + sine
//     modulator at non-integer ratio for clangorous partials)
//   • Glass-harmonica top harmonics (high sine choir)
//   • Prepared-piano plucks (short FM bursts with non-integer modulator)
//   • Waterphone glissandi (band-passed noise swept up & down)
//   • Bowed-cymbal swells (filtered noise crescendo, no sines but kept
//     for texture)
//   • Distant low-frequency drone bed (very slow detuned sine pair)
// All routed into a long convolver reverb (6 s tail) for the ambient
// wash.

function startAleatoricVoice(c) {
  const out = c.createGain();
  out.gain.value = 0.0;
  out.connect(c.destination);
  out.gain.linearRampToValueAtTime(0.65, c.currentTime + 3.0);

  const reverb = makeReverb(c, 6.5, 3.2);
  const wet = c.createGain(); wet.gain.value = 0.92;
  reverb.connect(wet).connect(out);
  const dry = c.createGain(); dry.gain.value = 0.30;
  dry.connect(out);

  // ---------------- Singing-bowl voice ------------------
  // Two-operator FM: carrier (sine) + modulator (sine) at a slightly
  // inharmonic ratio (~2.71 — close to e for non-musical clangor).  A
  // gentle vibrato on carrier makes the bowl breathe.  The modulation
  // index drops over the note's life so the note brightens to dull.
  const makeBowlVoice = () => {
    const carrier = c.createOscillator();
    const modulator = c.createOscillator();
    const modGain = c.createGain();
    const out_ = c.createGain();
    carrier.type = "sine"; modulator.type = "sine";
    carrier.frequency.value = 220;
    modulator.frequency.value = 220 * 2.71;
    modGain.gain.value = 180;
    modulator.connect(modGain).connect(carrier.frequency);
    out_.gain.value = 0.0001;
    // gentle vibrato
    const vib = c.createOscillator();
    const vibG = c.createGain();
    vib.type = "sine"; vib.frequency.value = 0.18 + Math.random() * 0.12;
    vibG.gain.value = 1.6;
    vib.connect(vibG).connect(carrier.detune);
    carrier.connect(out_);
    out_.connect(reverb);
    out_.connect(dry);
    carrier.start(); modulator.start(); vib.start();
    const setFreq = (f, when = c.currentTime, ramp = 5.0) => {
      // Crossfade pitch via a brief amplitude dip, like the strings
      // version did, so we don't hear an audible portamento.
      const dip = 0.7;
      const swap = when + dip;
      out_.gain.cancelScheduledValues(when);
      const cur = out_.gain.value || 0.0001;
      out_.gain.setValueAtTime(cur, when);
      out_.gain.linearRampToValueAtTime(0.0001, swap - 0.005);
      carrier.frequency.cancelScheduledValues(swap);
      modulator.frequency.cancelScheduledValues(swap);
      carrier.frequency.setValueAtTime(f, swap);
      modulator.frequency.setValueAtTime(f * 2.71, swap);
      // Modulation index slowly decays so the bowl darkens as it sustains
      modGain.gain.cancelScheduledValues(swap);
      modGain.gain.setValueAtTime(180, swap);
      modGain.gain.exponentialRampToValueAtTime(40, swap + ramp);
      out_.gain.setValueAtTime(0.0001, swap);
      out_.gain.linearRampToValueAtTime(0.18, swap + ramp);
    };
    const setInitial = (f) => {
      carrier.frequency.setValueAtTime(f, c.currentTime);
      modulator.frequency.setValueAtTime(f * 2.71, c.currentTime);
      out_.gain.setValueAtTime(0.0001, c.currentTime);
      out_.gain.exponentialRampToValueAtTime(0.18, c.currentTime + 4.5);
      modGain.gain.setValueAtTime(180, c.currentTime);
      modGain.gain.exponentialRampToValueAtTime(40, c.currentTime + 8.0);
    };
    return {
      setFreq, setInitial,
      stop: () => {
        try {
          out_.gain.cancelScheduledValues(c.currentTime);
          out_.gain.linearRampToValueAtTime(0.0001, c.currentTime + 1.6);
          setTimeout(() => { try { carrier.stop(); modulator.stop(); vib.stop(); } catch {} }, 1900);
        } catch {}
      },
    };
  };

  // 6 bowl voices stacked in different registers — 3 low, 3 upper.
  const bowls = [
    makeBowlVoice(), makeBowlVoice(), makeBowlVoice(),
    makeBowlVoice(), makeBowlVoice(), makeBowlVoice(),
  ];

  // ---------------- Cluster pool — same harmonic centres -----------
  const CLUSTERS = [
    [146.83, 155.56, 220.00, 311.13, 415.30, 466.16],
    [123.47, 130.81, 185.00, 261.63, 369.99, 415.30],
    [110.00, 116.54, 164.81, 233.08, 311.13, 369.99],
    [164.81, 174.61, 246.94, 349.23, 466.16, 523.25],
    [98.00,  103.83, 146.83, 207.65, 277.18, 311.13],
    [146.83, 174.61, 220.00, 293.66, 349.23, 440.00],
    [174.61, 207.65, 261.63, 349.23, 415.30, 523.25],
  ];

  let stopped = false;
  let chordIdx = 0;
  CLUSTERS[0].forEach((f, i) => bowls[i].setInitial(f));
  const advance = () => {
    if (stopped) return;
    chordIdx = (chordIdx + 1) % CLUSTERS.length;
    const t = c.currentTime + 0.05;
    CLUSTERS[chordIdx].forEach((f, i) => bowls[i].setFreq(f, t, 6.0));
    setTimeout(advance, 11000 + Math.random() * 6000);
  };
  setTimeout(advance, 11500);

  // ---------------- Glass-harmonica top harmonics ------------------
  // Crystalline high sine choir — a sparse 3-note voicing that drifts
  // over the bowls.  Pitch follows the current cluster's upper notes.
  const makeGlassVoice = () => {
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = "sine";
    o.frequency.value = 880;
    g.gain.value = 0.0001;
    o.connect(g).connect(reverb);
    o.start();
    return { o, g };
  };
  const glasses = [makeGlassVoice(), makeGlassVoice(), makeGlassVoice()];
  const glassTick = () => {
    if (stopped) return;
    const cluster = CLUSTERS[chordIdx];
    // pick three upper-cluster notes, octave up
    const upper = [cluster[3], cluster[4], cluster[5]].map((f) => f * 2);
    upper.forEach((f, i) => {
      const v = glasses[i];
      const t = c.currentTime + 0.05;
      v.o.frequency.cancelScheduledValues(t);
      v.o.frequency.linearRampToValueAtTime(f, t + 2.0);
      v.g.gain.cancelScheduledValues(t);
      v.g.gain.setValueAtTime(v.g.gain.value, t);
      v.g.gain.linearRampToValueAtTime(0.04 + Math.random() * 0.03, t + 2.5);
      v.g.gain.linearRampToValueAtTime(0.0001, t + 8.0);
    });
    setTimeout(glassTick, 14000 + Math.random() * 10000);
  };
  setTimeout(glassTick, 6000);

  // ---------------- Prepared-piano pluck -----------------------------
  // Short FM burst with inharmonic modulator (ratio 1.41, amount drops
  // exponentially in 200 ms) gives a "thumb-tack on the strings"
  // dampened-piano timbre.  Played at random cluster notes.
  const preparedPluck = () => {
    if (stopped) return;
    const cluster = CLUSTERS[chordIdx];
    const f = cluster[Math.floor(Math.random() * cluster.length)] * (Math.random() < 0.4 ? 2 : 1);
    const t0 = c.currentTime + 0.03;
    const car = c.createOscillator();
    const mod = c.createOscillator();
    const mg = c.createGain();
    const g = c.createGain();
    car.type = "sine"; mod.type = "sine";
    car.frequency.value = f;
    mod.frequency.value = f * 1.41;
    mg.gain.value = 220;
    mod.connect(mg).connect(car.frequency);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.22, t0 + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.4);
    mg.gain.setValueAtTime(220, t0);
    mg.gain.exponentialRampToValueAtTime(15, t0 + 0.20);
    car.connect(g).connect(reverb);
    car.start(t0); mod.start(t0);
    car.stop(t0 + 1.6); mod.stop(t0 + 1.6);
    setTimeout(preparedPluck, 7000 + Math.random() * 12000);
  };
  setTimeout(preparedPluck, 4000);

  // ---------------- Waterphone glissando ----------------------------
  // Band-passed noise swept up THEN down, very narrow Q for the eerie
  // metallic-water timbre.  Long, slow, sparse.
  const waterphone = () => {
    if (stopped) return;
    const t0 = c.currentTime + 0.05;
    const dur = 5.0 + Math.random() * 3.0;
    const len = Math.floor(c.sampleRate * (dur + 0.6));
    const buf = c.createBuffer(1, len, c.sampleRate);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < len; i++) ch[i] = (Math.random() * 2 - 1) * 0.6;
    const src = c.createBufferSource(); src.buffer = buf;
    const bp = c.createBiquadFilter();
    bp.type = "bandpass"; bp.Q.value = 28;
    const f0 = 600 + Math.random() * 800;
    const f1 = f0 * (2.5 + Math.random() * 1.5);
    bp.frequency.setValueAtTime(f0, t0);
    bp.frequency.exponentialRampToValueAtTime(f1, t0 + dur * 0.55);
    bp.frequency.exponentialRampToValueAtTime(f0 * 0.7, t0 + dur);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.18, t0 + dur * 0.35);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(bp).connect(g).connect(reverb);
    src.start(t0); src.stop(t0 + dur + 0.6);
    setTimeout(waterphone, 18000 + Math.random() * 18000);
  };
  setTimeout(waterphone, 9000);

  // ---------------- Distant low drone bed ---------------------------
  // Very slow detuned sine pair, very quiet — sets the floor of the
  // ambient wash.  No swells, just constant bass.
  (() => {
    const f = 55;
    [f, f * 1.005].forEach((freq) => {
      const o = c.createOscillator();
      const g = c.createGain();
      o.type = "sine";
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, c.currentTime);
      g.gain.exponentialRampToValueAtTime(0.10, c.currentTime + 6.0);
      o.connect(g).connect(reverb);
      o.start();
    });
  })();

  return () => {
    stopped = true;
    bowls.forEach((b) => b.stop());
    glasses.forEach((v) => {
      try { v.g.gain.linearRampToValueAtTime(0.0001, c.currentTime + 1.6); setTimeout(() => v.o.stop(), 1900); } catch {}
    });
    out.gain.cancelScheduledValues(c.currentTime);
    out.gain.linearRampToValueAtTime(0.0001, c.currentTime + 1.8);
    setTimeout(() => { try { out.disconnect(); } catch {} }, 2400);
  };
}

// ---------------------------------------------------------------- chess (classical guitar)
//
// Per user iter 51: replace the square-wave plucks with a CLASSICAL /
// ACOUSTIC GUITAR voice.  We approximate a nylon-string pluck via:
//   • 3 stacked oscillators tuned to the fundamental and its 2nd & 3rd
//     harmonics (sawtooth shaped down through a low-pass) — gives the
//     warm body of a plucked string without sounding like a synth pad.
//   • a brief noise burst at the very start (~12 ms) — the finger
//     attack on the string.
//   • a band-pass formant centred ~1.6 kHz — the resonance of the
//     guitar's wooden body.
//   • exponential decay with ~1.4 s release so notes ring naturally.
// No reverb, no chorus.

function startHarpsichordVoice(c) {
  const out = c.createGain();
  out.gain.value = 0.0;
  out.connect(c.destination);
  out.gain.linearRampToValueAtTime(0.40, c.currentTime + 1.5);

  // Body resonance (very subtle): a band-pass low-Q filter on the
  // shared output.  Keeps high partials from stinging.
  const body = c.createBiquadFilter();
  body.type = "bandpass";
  body.frequency.value = 1600;
  body.Q.value = 0.6;
  // Mix half body-filtered, half direct.
  const bodyGain = c.createGain(); bodyGain.gain.value = 0.55;
  const directGain = c.createGain(); directGain.gain.value = 0.65;
  body.connect(bodyGain).connect(out);
  directGain.connect(out);

  const pluck = (f, when, dur, peak = 0.10) => {
    // Three harmonic stacks: fundamental + 2× + 3× with decreasing
    // amplitude.  Sawtooth gives the bright pluck attack; we soften
    // it with a per-note low-pass tracked to the fundamental.
    const harms = [
      { mult: 1.0, type: "sawtooth", amp: 0.70 },
      { mult: 2.0, type: "triangle", amp: 0.30 },
      { mult: 3.0, type: "triangle", amp: 0.14 },
    ];
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(Math.min(4000, f * 8), when);
    // Sweep the low-pass DOWN over the note's life — this is what
    // turns a sawtooth into a "plucked-string" timbre.
    lp.frequency.exponentialRampToValueAtTime(Math.max(600, f * 2.2), when + dur);
    lp.Q.value = 0.6;

    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, when);
    g.gain.exponentialRampToValueAtTime(peak, when + 0.008);  // soft finger attack
    g.gain.exponentialRampToValueAtTime(peak * 0.55, when + 0.20);
    g.gain.exponentialRampToValueAtTime(0.0001, when + dur);

    harms.forEach(({ mult, type, amp }) => {
      const o = c.createOscillator();
      o.type = type;
      o.frequency.value = f * mult;
      // Tiny detune for the gentle chorus a real string produces.
      o.detune.value = (mult === 1.0 ? -3 : mult === 2.0 ? +4 : -5);
      const ag = c.createGain();
      ag.gain.value = amp;
      o.connect(ag).connect(lp);
      o.start(when); o.stop(when + dur + 0.04);
    });
    lp.connect(g);
    g.connect(directGain);
    g.connect(body);

    // Finger-attack noise burst — short, high-passed, mixes with the
    // harmonic stack to give the "click" of nail-on-string.
    const buf = c.createBuffer(1, c.sampleRate * 0.02, c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 2.5);
    }
    const src = c.createBufferSource(); src.buffer = buf;
    const hp = c.createBiquadFilter();
    hp.type = "highpass"; hp.frequency.value = 2200;
    const ng = c.createGain(); ng.gain.value = 0.10;
    src.connect(hp).connect(ng).connect(directGain);
    src.start(when); src.stop(when + 0.025);
  };

  // Major-chord pool with surprising chord changes — root, 3, 5, oct.
  const CHORDS = [
    [261.63, 329.63, 392.00, 523.25],   // C  major
    [311.13, 392.00, 466.16, 622.25],   // E♭ major
    [349.23, 440.00, 523.25, 698.46],   // F  major
    [415.30, 523.25, 622.25, 830.61],   // A♭ major
    [220.00, 277.18, 329.63, 440.00],   // A  major
    [293.66, 369.99, 440.00, 587.33],   // D  major
    [369.99, 466.16, 554.37, 739.99],   // F# major
    [196.00, 246.94, 293.66, 392.00],   // G  major
    [233.08, 293.66, 349.23, 466.16],   // B♭ major
  ];

  let stopped = false;
  let idx = 0;
  const cleanups = [];
  const playArp = () => {
    if (stopped) return;
    const chord = CHORDS[idx];
    idx = (idx + Math.floor(1 + Math.random() * 3)) % CHORDS.length;
    const t0 = c.currentTime + 0.05;
    // Up-then-down arpeggio.  Nylon-string notes ring ~1.4 s and we
    // play them ~0.42 s apart so each one decays naturally into the
    // next without overlapping into mush.
    const order = [chord[0], chord[1], chord[2], chord[3], chord[2], chord[1]];
    const noteSpacing = 0.42;
    const noteDur = 1.40;
    order.forEach((f, i) => {
      pluck(f, t0 + i * noteSpacing, noteDur, 0.10);
    });
    if (Math.random() < 0.30) {
      pluck(chord[3] * 1.5, t0 + 6 * noteSpacing - 0.04, 1.0, 0.07);
    }
    const nextIn = (order.length * noteSpacing + 0.85 + Math.random() * 0.45) * 1000;
    const id = setTimeout(playArp, nextIn);
    cleanups.push(() => clearTimeout(id));
  };
  setTimeout(playArp, 200);

  return () => {
    stopped = true;
    cleanups.forEach((fn) => { try { fn(); } catch {} });
    out.gain.cancelScheduledValues(c.currentTime);
    out.gain.linearRampToValueAtTime(0.0001, c.currentTime + 0.8);
    setTimeout(() => { try { out.disconnect(); } catch {} }, 1200);
  };
}

const VOICE_BY_THEME = {
  // Chess now plays a relaxing major-chord harpsichord arpeggio.
  chess:    startHarpsichordVoice,
  // Pente: chess choir + high synth-pad bells overlay (more ambient,
  // the prior square/triangle drones have been retired per user spec).
  pente:    startPenteAmbientVoice,
  estampie: startEstampieVoice,
  phantasm: startPhantasmVoice,
  aleatoric: startAleatoricVoice,
};

// ---------------------------------------------------------------- hook
//
// Module-level shared state — a single backdrop voice runs across page
// transitions.  Navigating between two pages that request the same
// theme (e.g. /games/chess → /games/chess/host) is a no-op; the
// existing voice keeps playing.  Only when the theme actually changes
// (Chess → Pente, Tavern → Studio) do we cross-fade by stopping the
// old voice and starting the new one.
let CURRENT_THEME = null;
let CURRENT_STOP = null;
let MOUNT_COUNT = 0;

export function useEerieBackdrop(theme /* "chess" | "pente" | "estampie" | "phantasm" | "aleatoric" */) {
  useEffect(() => {
    if (isEerieMuted()) return;
    const c = ctx();
    if (!c) return;

    MOUNT_COUNT += 1;

    const begin = () => {
      try { if (c.state === "suspended") c.resume(); } catch {}
      // If a different voice is already running, swap it out.
      if (CURRENT_THEME !== theme) {
        if (CURRENT_STOP) {
          try { CURRENT_STOP(); } catch {}
          CURRENT_STOP = null;
        }
        const start = VOICE_BY_THEME[theme] || startChessVoice;
        CURRENT_STOP = start(c);
        CURRENT_THEME = theme;
      }
    };
    begin();
    const onGesture = () => { begin(); window.removeEventListener("pointerdown", onGesture); window.removeEventListener("keydown", onGesture); };
    window.addEventListener("pointerdown", onGesture, { once: true });
    window.addEventListener("keydown", onGesture, { once: true });

    return () => {
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("keydown", onGesture);
      MOUNT_COUNT = Math.max(0, MOUNT_COUNT - 1);
      // Defer teardown by a microtask: a same-theme route change will
      // remount immediately and bump MOUNT_COUNT back up before the
      // queued check fires, so we leave the voice running.  Only when
      // the count is genuinely zero (user left every backdrop-using
      // page) do we tear down.
      setTimeout(() => {
        if (MOUNT_COUNT === 0 && CURRENT_STOP) {
          try { CURRENT_STOP(); } catch {}
          CURRENT_STOP = null;
          CURRENT_THEME = null;
        }
      }, 80);
    };
  }, [theme]);
}
